export interface RegistrationStream {
  id: string;
  name: string;
  stateRegisteredId?: string | null;
  stateRegistrationStatus?: string | null;
  stateRegisteredAt?: string | null;
}

const STATUS_RANK: Record<string, number> = {
  NOT_REGISTERED: 0,
  IN_PROGRESS: 1,
  REGISTERED: 2,
};

function rank(status: string | null | undefined): number {
  if (status == null) return -1;
  return STATUS_RANK[status] ?? -1;
}

function timestamp(at: string | null | undefined): number {
  if (at == null) return 0;
  const parsed = new Date(at).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Streams that carry a non-null state registration id. */
export function getRegistrationCandidates(streams: RegistrationStream[]): RegistrationStream[] {
  return streams.filter((stream) => stream.stateRegisteredId != null);
}

/**
 * True when the registered streams disagree on the triple and the user must
 * pick which registration survives. False when none/one is registered or all
 * registered streams share an identical id+status+timestamp.
 */
export function needsRegistrationChoice(streams: RegistrationStream[]): boolean {
  const candidates = getRegistrationCandidates(streams);
  if (candidates.length < 2) return false;
  const distinctTriples = new Set(
    candidates.map(
      (c) =>
        `${c.stateRegisteredId}|${c.stateRegistrationStatus ?? ""}|${c.stateRegisteredAt ?? ""}`
    )
  );
  return distinctTriples.size > 1;
}

/** The id of the registered stream whose registration should be pre-selected. */
export function defaultRegistrationSourceId(streams: RegistrationStream[]): string | undefined {
  const candidates = getRegistrationCandidates(streams);
  if (candidates.length === 0) return undefined;
  const winner = candidates.reduce((best, candidate) => {
    const bestRank = rank(best.stateRegistrationStatus);
    const candidateRank = rank(candidate.stateRegistrationStatus);
    if (candidateRank !== bestRank) {
      return candidateRank > bestRank ? candidate : best;
    }
    return timestamp(candidate.stateRegisteredAt) > timestamp(best.stateRegisteredAt)
      ? candidate
      : best;
  });
  return winner.id;
}
